import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Link2, UserPlus } from 'lucide-react';
import Modal from './Modal';
import { apiClient } from '../apiClient';

function ShareAccessModal({ open, onClose, resourceType, resourceId, resourceName, isOwner }) {
  const [tab, setTab] = useState('invite');
  const [inviteLink, setInviteLink] = useState('');
  const [inviteId, setInviteId] = useState('');
  const [inviteMeta, setInviteMeta] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [expiresDays, setExpiresDays] = useState('');
  const [maxUses, setMaxUses] = useState('1');
  const [unlimitedUses, setUnlimitedUses] = useState(false);
  const [copied, setCopied] = useState(false);

  const [accessList, setAccessList] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [granting, setGranting] = useState(false);

  const reset = () => {
    setTab('invite');
    setInviteError('');
    setInviteMeta(null);
    setInviteLink('');
    setInviteId('');
    setExpiresDays('');
    setMaxUses('1');
    setUnlimitedUses(false);
    setAccessError('');
    setIdentifier('');
  };

  useEffect(() => {
    if (!open) return;
    reset();
  }, [open]);

  useEffect(() => {
    if (!open || !isOwner || !resourceId) return;

    const loadAccess = async () => {
      setAccessLoading(true);
      setAccessError('');
      try {
        const data = await apiClient.get('/api/access/list', {
          resourceType,
          resourceId
        });
        setAccessList(data);
      } catch (err) {
        setAccessError(err.message || 'Unable to load access list.');
      } finally {
        setAccessLoading(false);
      }
    };

    const loadInvites = async () => {
      setInviteLoading(true);
      setInviteError('');
      try {
        const data = await apiClient.get('/api/invites', {
          resourceType,
          resourceId
        });
        if (data && data.length) {
          const latest = [...data].sort((a, b) => {
            const aTime = new Date(a.created_at || 0).getTime();
            const bTime = new Date(b.created_at || 0).getTime();
            return bTime - aTime;
          })[0];
          setInviteId(latest.id);
          setInviteMeta(latest);
        }
      } catch (err) {
        setInviteError(err.message || 'Unable to load invites.');
      } finally {
        setInviteLoading(false);
      }
    };

    loadAccess();
    loadInvites();
  }, [open, isOwner, resourceId, resourceType]);

  const handleCreateInvite = async () => {
    setInviteLoading(true);
    setInviteError('');
    try {
      const expiresAt = expiresDays ? new Date(Date.now() + Number(expiresDays) * 86400000).toISOString() : null;
      const maxUsesValue = unlimitedUses ? 0 : (maxUses ? Number(maxUses) : 1);
      const data = await apiClient.post('/api/invites/create', {
        resourceType,
        resourceId,
        expiresAt,
        maxUses: Number.isNaN(maxUsesValue) ? 1 : maxUsesValue
      });
      setInviteLink(data.inviteUrl);
      setInviteId(data.inviteId);
      setInviteMeta({ created_at: new Date().toISOString() });
    } catch (err) {
      setInviteError(err.message || 'Failed to create invite.');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRevokeInvite = async () => {
    if (!inviteId) return;
    setInviteLoading(true);
    setInviteError('');
    try {
      await apiClient.post('/api/invites/revoke', { inviteId });
      setInviteId('');
      setInviteLink('');
      setInviteMeta(null);
    } catch (err) {
      setInviteError(err.message || 'Failed to revoke invite.');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setInviteError('Unable to copy invite link.');
    }
  };

  const handleGrantAccess = async () => {
    if (!identifier.trim()) return;
    setGranting(true);
    setAccessError('');
    try {
      const data = await apiClient.post('/api/access/grant', {
        resourceType,
        resourceId,
        identifier: identifier.trim()
      });
      setAccessList((prev) => [...prev, { ...data, user_id: data.user_id }]);
      setIdentifier('');
    } catch (err) {
      setAccessError(err.message || 'Failed to grant access.');
    } finally {
      setGranting(false);
    }
  };

  const handleRevokeAccess = async (userId) => {
    const confirmed = window.confirm('Remove access for this user?');
    if (!confirmed) return;
    try {
      await apiClient.post('/api/access/revoke', {
        resourceType,
        resourceId,
        granteeUserId: userId
      });
      setAccessList((prev) => prev.filter((entry) => entry.user_id !== userId));
    } catch (err) {
      setAccessError(err.message || 'Failed to revoke access.');
    }
  };

  const resourceLabel = useMemo(() => {
    if (!resourceName) return 'this item';
    return `"${resourceName}"`;
  }, [resourceName]);

  return (
    <Modal
      open={open}
      title={`Share ${resourceLabel}`}
      onClose={onClose}
      footer={(
        <div className="modal-actions">
          <button className="btn-secondary" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      )}
    >
      {!isOwner ? (
        <div className="share-locked">
          <p>You have view-only access to this {resourceType}. Only the owner can manage sharing.</p>
        </div>
      ) : (
        <div className="share-modal">
          <div className="share-tabs">
            <button
              type="button"
              className={`share-tab ${tab === 'invite' ? 'active' : ''}`}
              onClick={() => setTab('invite')}
            >
              Invite Link
            </button>
            <button
              type="button"
              className={`share-tab ${tab === 'people' ? 'active' : ''}`}
              onClick={() => setTab('people')}
            >
              People with Access
            </button>
          </div>

          {tab === 'invite' && (
            <div className="share-panel">
              <div className="share-row">
                <div className="share-field">
                  <label>Expiration (days)</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Optional"
                    value={expiresDays}
                    onChange={(e) => setExpiresDays(e.target.value)}
                  />
                </div>
                <div className="share-field">
                  <label>Max uses</label>
                  <input
                    type="number"
                    min="0"
                    value={unlimitedUses ? '0' : maxUses}
                    onChange={(e) => setMaxUses(e.target.value)}
                    disabled={unlimitedUses}
                  />
                </div>
                <button
                  type="button"
                  className={`share-toggle ${unlimitedUses ? 'active' : ''}`}
                  onClick={() => setUnlimitedUses((prev) => !prev)}
                >
                  Unlimited uses
                </button>
                <button
                  type="button"
                  className="btn-primary btn-compact"
                  onClick={handleCreateInvite}
                  disabled={inviteLoading}
                >
                  <Link2 className="w-4 h-4" /> {inviteLoading ? 'Creating...' : 'Create Link'}
                </button>
              </div>

              {inviteLink && (
                <div className="share-link">
                  <span className="share-link-text">{inviteLink}</span>
                  <div className="share-copy-wrap">
                    <button
                      type="button"
                      className={`share-copy ${copied ? 'copied' : ''}`}
                      onClick={handleCopy}
                      aria-label="Copy invite link"
                      data-tooltip={copied ? 'Copied!' : 'Copy invite link'}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    {copied && <span className="share-copied">Copied</span>}
                  </div>
                </div>
              )}

              {!inviteLink && inviteMeta && (
                <div className="share-note">
                  Active invite created. For security, links are shown only once. You can revoke and create a new link.
                </div>
              )}

              {inviteId && (
                <button type="button" className="btn-secondary btn-compact danger" onClick={handleRevokeInvite}>
                  Revoke Invite
                </button>
              )}

              {inviteError && <div className="share-error">{inviteError}</div>}
            </div>
          )}

          {tab === 'people' && (
            <div className="share-panel">
              <div className="share-row">
                <div className="share-field grow">
                  <label>Grant access by email or @handle</label>
                  <input
                    type="text"
                    placeholder="name@domain.com or @handle"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn-primary btn-compact"
                  onClick={handleGrantAccess}
                  disabled={granting}
                >
                  <UserPlus className="w-4 h-4" /> {granting ? 'Adding...' : 'Grant Access'}
                </button>
              </div>

              {accessLoading ? (
                <div className="text-sm text-gray-400">Loading access…</div>
              ) : (
                <div className="share-list">
                  {accessList.length === 0 ? (
                    <div className="share-empty">No one has access yet.</div>
                  ) : (
                    accessList.map((entry) => (
                      <div className="share-person" key={entry.user_id}>
                        <div className="share-avatar">
                          {entry.avatar_url ? (
                            <img src={entry.avatar_url} alt={entry.name || entry.username} />
                          ) : (
                            <span>{(entry.name || entry.username || 'U').slice(0, 2).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="share-meta">
                          <div className="share-name">{entry.name || entry.username}</div>
                          <div className="share-sub">
                            {entry.username ? `@${entry.username}` : entry.email}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn-secondary btn-compact danger"
                          onClick={() => handleRevokeAccess(entry.user_id)}
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {accessError && <div className="share-error">{accessError}</div>}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default ShareAccessModal;
